import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './contexts/authState';
import { changeAppLanguage, formatErrorWithCode, languageOptions, type AppLanguage } from './i18n';
import { bubbleGameDeepLink, bubbleGameFurnaceDeepLink, walletAppDeepLink } from './lib/config';
import { parseSwapDeepLink, resolveBubbleGameLink } from './lib/appDeepLink';
import {
  formatOnChainBalance,
  getOnChainBalances,
  ON_CHAIN_TOKEN_ADDRESSES,
  type OnChainTokenBalance,
  type OnChainTokenSymbol,
} from './lib/blockchain';
import SendTokenModal, { type SendTokenOption } from './components/SendTokenModal';
import SwapModal, { type SwapTokenOption } from './components/SwapModal';
import ReceiveAddressModal from './components/ReceiveAddressModal';
import ClaimModal from './components/ClaimModal';
import BbtInfoModal from './components/BbtInfoModal';
import BbgInfoModal from './components/BbgInfoModal';
import GgmomomoInfoModal from './components/GgmomomoInfoModal';
import WldInfoModal from './components/WldInfoModal';
import BubbleGameUniverseModal from './components/BubbleGameUniverseModal';
import Flight404InfoModal from './components/Flight404InfoModal';
import BbgTransferModal, { type BbgTransferMode } from './components/BbgTransferModal';
import WorldTransactionToast from './components/WorldTransactionToast';
import WalletSourceIcon from './components/WalletSourceIcon';
import { getInGameBbgBalance, type InGameBbgBalance } from './lib/inGameWallet';
import { getFlight404FcashWallet, type FcashWallet } from './lib/flight404Wallet';
import {
  connectMetaMask,
  disconnectMetaMask,
  getConnectedMetaMaskAddress,
  getMetaMaskProvider,
} from './lib/metamask';
import type { WalletBalanceSource } from './lib/walletSource';
import {
  getOnChainTransactionHistoryPage,
  type OnChainTransactionHistoryCursor,
  type OnChainTransactionRecord,
} from './lib/transactionHistory';
import {
  getBubbleWalletSpendingActivity,
  type BubbleWalletActivityRecord,
} from './lib/walletActivity';
import {
  formatUsdPrice,
  formatUsdValue,
  getOnChainTokenPrices,
  type TokenPrice,
} from './lib/tokenPrices';
import {
  IN_GAME_BBG_LOGO,
  ON_CHAIN_BBG_LOGO,
  ON_CHAIN_GGMOMOMO_LOGO,
  ON_CHAIN_WLD_LOGO,
} from './lib/tokenLogos';
import { supabase } from './lib/supabaseClient';
import {
  cancelStaleWithdrawal,
  getPendingWithdrawals,
  type PendingWithdrawal,
} from './lib/withdrawalRecovery';

type WalletTab = 'in-game-wallet' | 'on-chain-wallet' | 'activity' | 'settings';
type WalletPageView = 'in-game-wallet' | 'on-chain-wallet';
type WalletTabLabel = 'gameAssets' | 'cryptoAssets' | 'activity' | 'settings';
type NavigationIconName = 'game' | 'crypto' | 'activity' | 'settings';

type SwapPreset = {
  inputSymbol: OnChainTokenSymbol;
  outputSymbol: OnChainTokenSymbol;
};

const tabs: Array<{ id: WalletTab; icon: NavigationIconName; labelKey: WalletTabLabel }> = [
  { id: 'in-game-wallet', icon: 'game', labelKey: 'gameAssets' },
  { id: 'on-chain-wallet', icon: 'crypto', labelKey: 'cryptoAssets' },
  { id: 'activity', icon: 'activity', labelKey: 'activity' },
  { id: 'settings', icon: 'settings', labelKey: 'settings' },
];

const tokenCards: Array<{
  symbol: OnChainTokenSymbol;
  label: string;
  logo: string;
  tone: 'gold' | 'mint' | 'pink' | 'purple';
  contractAddress: string;
}> = [
  { symbol: 'WLD', label: 'World Coin', logo: ON_CHAIN_WLD_LOGO, tone: 'mint', contractAddress: ON_CHAIN_TOKEN_ADDRESSES.WLD },
  { symbol: 'BBG', label: 'Bubble Game Token', logo: ON_CHAIN_BBG_LOGO, tone: 'gold', contractAddress: ON_CHAIN_TOKEN_ADDRESSES.BBG },
  { symbol: 'BBT', label: 'Bubble Tea Coin', logo: '/image/logo/bbtcoin_newlogo.png', tone: 'pink', contractAddress: ON_CHAIN_TOKEN_ADDRESSES.BBT },
  { symbol: 'GGMOMOMO', label: 'GGmomomo Coin', logo: ON_CHAIN_GGMOMOMO_LOGO, tone: 'purple', contractAddress: ON_CHAIN_TOKEN_ADDRESSES.GGMOMOMO },
] as const;

const gameAssetCards = [
  { symbol: 'BBG', label: 'Bubble Game Universe', logo: IN_GAME_BBG_LOGO, tone: 'gold' },
  { symbol: 'F-CASH', label: 'FLIGHT 404', logo: '/image/logo/flight404.png', tone: 'purple' },
] as const;

function NavigationIcon({ name }: { name: NavigationIconName }) {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  };

  if (name === 'game') {
    return (
      <svg {...commonProps}>
        <path d="M7.2 9.2h9.6a3 3 0 0 1 2.8 4l-1.1 2.8a2.4 2.4 0 0 1-4.2.55l-.9-1.15h-2.8l-.9 1.15a2.4 2.4 0 0 1-4.2-.55l-1.1-2.8a3 3 0 0 1 2.8-4Z" />
        <path d="M8 11.5v3M6.5 13h3M15.5 12.3h.01M17.5 14.2h.01" />
      </svg>
    );
  }

  if (name === 'crypto') {
    return (
      <svg {...commonProps}>
        <path d="m12 3.75 7.25 8.25L12 20.25 4.75 12 12 3.75Z" />
        <path d="M8.5 12h7" />
      </svg>
    );
  }

  if (name === 'activity') {
    return (
      <svg {...commonProps}>
        <path d="M4.5 13.5h3l1.7-5 3.2 9 1.8-5h5.3" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <circle cx="12" cy="12" r="3.25" />
      <path d="M12 3.5v2.25M12 18.25v2.25M3.5 12h2.25M18.25 12h2.25M6 6l1.6 1.6M16.4 16.4 18 18M18 6l-1.6 1.6M7.6 16.4 6 18" />
    </svg>
  );
}

function SummaryIcon({ name }: { name: 'claim' | 'swap' | 'send' | 'receive' }) {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  };

  if (name === 'claim') {
    return (
      <svg
        {...commonProps}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M16.25 7.3c-.7-1.1-1.95-1.7-3.8-1.7-2.1 0-3.4 1-3.4 2.5 0 3.8 7.2 1.8 7.2 5.6 0 1.6-1.35 2.5-3.7 2.5-1.9 0-3.1-.65-3.8-1.9" />
        <path d="M12 4.35L12 17.2" />
      </svg>
    );
  }

  if (name === 'swap') {
    return (
      <svg {...commonProps}>
        <path d="M7 7h10m0 0-3-3m3 3-3 3M17 17H7m0 0 3-3m-3 3 3 3" />
      </svg>
    );
  }

  if (name === 'send') {
    return (
      <svg {...commonProps}>
        <path d="m21 3-7.2 18-3.6-7.2L3 10.2 21 3Z" />
        <path d="m10.2 13.8 5.1-5.1" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M12 4v10m0 0 4-4m-4 4-4-4M5 19h14" />
    </svg>
  );
}

function formatWalletAddress(address: string | null, fallback = '—'): string {
  return address ? `${address.slice(0, 6)}…${address.slice(-4)}` : fallback;
}

function AssetRow({
  symbol,
  label,
  logo,
  tone,
  contractAddress,
  balance,
  isLoading,
  walletAddress,
  price,
  isPriceLoading,
  onClick,
  onClickAriaLabel,
}: (typeof tokenCards)[number] & {
  balance?: OnChainTokenBalance;
  isLoading: boolean;
  walletAddress: string | null;
  price?: TokenPrice;
  isPriceLoading: boolean;
  onClick?: () => void;
  onClickAriaLabel?: string;
}) {
  const { t } = useTranslation();
  const balanceValue = !walletAddress
    ? '—'
    : isLoading
      ? t('common.loading')
      : balance?.error
        ? t('common.queryFailed')
        : balance
          ? formatOnChainBalance(balance.balance)
          : '—';
  const priceValue = isPriceLoading
    ? t('common.loading')
    : !price?.usd
      ? t('overview.priceNotAvailable')
      : `${formatUsdPrice(price.usd)} / ${symbol}`;
  const usdValue = walletAddress && balance && price?.usd
    ? formatUsdValue(Number(balance.balance) * price.usd)
    : null;

  const rowContent = (
    <>
      <div className="asset-row-heading">
        <img className="token-logo" src={logo} alt={`${label} logo`} />
        <div>
          <h3>{symbol}</h3>
          <p className="asset-name">{label}</p>
        </div>
      </div>
      <div className="asset-row-value">
        <strong>{balanceValue}</strong>
        <span className="asset-row-usd">{usdValue ?? priceValue}</span>
      </div>
    </>
  );

  if (!onClick) {
    return (
      <article
        className={`asset-row asset-row-${tone}`}
        data-token-address={contractAddress ?? undefined}
      >
        {rowContent}
      </article>
    );
  }

  return (
    <button
      className={`asset-row asset-row-${tone} asset-row-button`}
      type="button"
      data-token-address={contractAddress ?? undefined}
      onClick={onClick}
      aria-label={onClickAriaLabel}
    >
      {rowContent}
    </button>
  );
}

function GameAssetRow({
  symbol,
  label,
  logo,
  tone,
  balance,
  isLoading,
  error,
  onClick,
  onClickAriaLabel,
}: (typeof gameAssetCards)[number] & {
  balance?: InGameBbgBalance | FcashWallet | null;
  isLoading: boolean;
  error: string | null;
  onClick?: () => void;
  onClickAriaLabel?: string;
}) {
  const { t } = useTranslation();
  const value = !balance && !isLoading && !error
      ? '—'
      : isLoading
        ? t('common.loading')
        : error
          ? t('common.queryFailed')
          : balance?.balance.toLocaleString('en-US') ?? '—';

  const rowContent = (
    <>
      <div className="asset-row-heading">
        <img className="token-logo" src={logo} alt={`${label} logo`} />
        <div>
          <h3>{symbol}</h3>
          <p className="asset-name">{label}</p>
        </div>
      </div>
      <div className="asset-row-value">
        <strong>{value}</strong>
      </div>
    </>
  );

  if (!onClick) {
    return (
      <article className={`asset-row game-asset-row asset-row-${tone}`}>
        {rowContent}
      </article>
    );
  }

  return (
    <button
      className={`asset-row game-asset-row asset-row-${tone} asset-row-button`}
      type="button"
      onClick={onClick}
      aria-label={onClickAriaLabel}
    >
      {rowContent}
    </button>
  );
}

function PendingWithdrawals({
  withdrawals,
  cancellingId,
  onCancel,
}: {
  withdrawals: PendingWithdrawal[];
  cancellingId: string | null;
  onCancel: (withdrawal: PendingWithdrawal) => void;
}) {
  const { t } = useTranslation();
  if (withdrawals.length === 0) return null;

  return (
    <div className="game-wallet-pending" role="status">
      <strong>{t('transfer.pending')}</strong>
      {withdrawals.map((withdrawal) => (
        <div className="game-wallet-pending-row" key={withdrawal.id}>
          <span>{withdrawal.amount} BBG</span>
          {withdrawal.canCancel ? (
            <button type="button" onClick={() => onCancel(withdrawal)} disabled={cancellingId !== null}>
              {cancellingId === withdrawal.id ? t('common.loading') : t('transfer.unfreeze')}
            </button>
          ) : (
            <span>
              {withdrawal.isStale && withdrawal.claimCheck === 'unknown'
                ? t('transfer.pendingStatusUnavailable')
                : withdrawal.isStale
                  ? t('common.loading')
                  : withdrawal.status === 'requested'
                    ? t('transfer.pendingNoDuplicate')
                    : t('transfer.finalizePending')}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function WalletAssetsPage({
  view,
  balances,
  isLoading,
  onRefresh,
  walletAddress,
  balanceSource,
  metaMaskAddress,
  isMetaMaskLoading,
  metaMaskError,
  onSelectBalanceSource,
  onConnectMetaMask,
  onOpenSendModal,
  onOpenSwapModal,
  onOpenBbtInfoModal,
  onOpenBbgInfoModal,
  onOpenGgmomomoInfoModal,
  onOpenWldInfoModal,
  onOpenBubbleGameUniverseModal,
  onOpenFlight404InfoModal,
  bubbleGameLink,
  onOpenReceiveModal,
  onOpenClaimModal,
  bbgGameBalance,
  fcashGameBalance,
  isGameBalancesLoading,
  bbgGameBalanceError,
  fcashGameBalanceError,
  onRefreshGameBalances,
  onOpenBbgTransfer,
  pendingWithdrawals,
  cancellingWithdrawalId,
  onCancelWithdrawal,
  prices,
  isPricesLoading,
  priceError,
  priceFetchedAt,
}: {
  view: WalletPageView;
  balances: Partial<Record<OnChainTokenSymbol, OnChainTokenBalance>>;
  isLoading: boolean;
  onRefresh: () => void;
  walletAddress: string | null;
  balanceSource: WalletBalanceSource;
  metaMaskAddress: string | null;
  isMetaMaskLoading: boolean;
  metaMaskError: string | null;
  onSelectBalanceSource: (source: WalletBalanceSource) => void;
  onConnectMetaMask: () => Promise<void>;
  onOpenSendModal: () => void;
  onOpenSwapModal: () => void;
  onOpenBbtInfoModal: () => void;
  onOpenBbgInfoModal: () => void;
  onOpenGgmomomoInfoModal: () => void;
  onOpenWldInfoModal: () => void;
  onOpenBubbleGameUniverseModal: () => void;
  onOpenFlight404InfoModal: () => void;
  bubbleGameLink: string | null;
  onOpenReceiveModal: () => void;
  onOpenClaimModal: () => void;
  bbgGameBalance: InGameBbgBalance | null;
  fcashGameBalance: FcashWallet | null;
  isGameBalancesLoading: boolean;
  bbgGameBalanceError: string | null;
  fcashGameBalanceError: string | null;
  onRefreshGameBalances: () => void;
  onOpenBbgTransfer: (mode: BbgTransferMode) => void;
  pendingWithdrawals: PendingWithdrawal[];
  cancellingWithdrawalId: string | null;
  onCancelWithdrawal: (withdrawal: PendingWithdrawal) => void;
  prices: Partial<Record<OnChainTokenSymbol, TokenPrice>>;
  isPricesLoading: boolean;
  priceError: string | null;
  priceFetchedAt: number | null;
}) {
  const { t, i18n } = useTranslation();
  const assetsLoading = isLoading || isPricesLoading;
  const summaryBalance = useMemo(() => {
    if (!walletAddress) return 'USD $—';
    if (assetsLoading) return t('common.loading');

    const nonZeroAssets = tokenCards.filter((token) => {
      const balance = balances[token.symbol];
      return balance && Number(balance.balance) > 0;
    });

    const pricedAssets = nonZeroAssets.filter((token) => {
      const price = prices[token.symbol];
      return Boolean(price?.usd);
    });

    if (pricedAssets.length === 0) return 'USD $—';

    const totalUsdValue = pricedAssets.reduce((total, token) => {
      const balance = balances[token.symbol];
      const price = prices[token.symbol]?.usd;
      return total + (balance && price ? Number(balance.balance) * price : 0);
    }, 0);

    return formatUsdValue(totalUsdValue);
  }, [assetsLoading, balances, prices, t, walletAddress]);
  const priceStatus = isPricesLoading
    ? t('overview.priceLoading')
    : priceError
      ? t('overview.priceUnavailable')
      : priceFetchedAt
        ? t('overview.priceUpdatedAt', {
          time: new Date(priceFetchedAt).toLocaleTimeString(i18n.resolvedLanguage === 'zh-Hant' ? 'zh-HK' : 'en-US', {
            hour: '2-digit',
            minute: '2-digit',
          }),
        })
        : t('overview.priceNotAvailable');

  return (
    <>
      {view === 'on-chain-wallet' && (
        <>
          <section className="summary-card">
            <p className="summary-total-label">{t('overview.totalMarketValue')}</p>
            <p className="summary-balance">{summaryBalance}</p>
            <div className="summary-actions" aria-label={t('overview.walletActions')}>
              <button className="summary-action" type="button" onClick={onOpenClaimModal}>
                <span className="summary-action-icon"><SummaryIcon name="claim" /></span>
                <span>{t('overview.claim')}</span>
              </button>
              <button className="summary-action" type="button" onClick={onOpenSwapModal}>
                <span className="summary-action-icon"><SummaryIcon name="swap" /></span>
                <span>{t('overview.swap')}</span>
              </button>
              <button className="summary-action" type="button" onClick={onOpenSendModal}>
                <span className="summary-action-icon"><SummaryIcon name="send" /></span>
                <span>{t('overview.send')}</span>
              </button>
              <button className="summary-action" type="button" onClick={onOpenReceiveModal}>
                <span className="summary-action-icon"><SummaryIcon name="receive" /></span>
                <span>{t('overview.receive')}</span>
              </button>
            </div>
          </section>

          <section className="section-block">
            <div className="section-heading">
              <div>
                <p className="eyebrow">ON-CHAIN ASSETS</p>
                <h2>{t('overview.onChainAssets')}</h2>
                <p className="price-status-caption">{priceStatus}</p>
              </div>
              <button className="text-button" type="button" onClick={onRefresh} disabled={assetsLoading}>
                {assetsLoading ? t('common.loading') : t('overview.updateAssets')}
              </button>
            </div>
            <div className="wallet-source-tabs" role="tablist" aria-label={t('overview.selectAssetAccount')}>
              <button
                className={balanceSource === 'world-app' ? 'wallet-source-tab is-active' : 'wallet-source-tab'}
                type="button"
                role="tab"
                aria-selected={balanceSource === 'world-app'}
                onClick={() => onSelectBalanceSource('world-app')}
              >
                <WalletSourceIcon source="world-app" />
                <span>World App</span>
              </button>
              <button
                className={balanceSource === 'metamask' ? 'wallet-source-tab is-active' : 'wallet-source-tab'}
                type="button"
                role="tab"
                aria-selected={balanceSource === 'metamask'}
                onClick={() => onSelectBalanceSource('metamask')}
              >
                <WalletSourceIcon source="metamask" />
                <span>MetaMask</span>
              </button>
            </div>
            <div className="wallet-source-status">
              <span>{t('overview.currentViewing', { source: balanceSource === 'world-app' ? 'World App' : 'MetaMask' })}</span>
              <code>{formatWalletAddress(balanceSource === 'metamask' ? metaMaskAddress : walletAddress, t('common.notConnected'))}</code>
            </div>
            {balanceSource === 'metamask' && !metaMaskAddress && (
              <div className="wallet-source-empty">
                {metaMaskError && <p className="send-token-error" role="alert">{metaMaskError}</p>}
                <button className="auth-button" type="button" onClick={() => void onConnectMetaMask()} disabled={isMetaMaskLoading}>
                  {isMetaMaskLoading ? t('common.connect') : t('common.connectMetaMask')}
                </button>
              </div>
            )}
            <div className="asset-list">
              {tokenCards.map((token) => (
                <AssetRow
                  key={token.symbol}
                  {...token}
                  balance={balances[token.symbol]}
                  isLoading={isLoading}
                  walletAddress={walletAddress}
                  price={prices[token.symbol]}
                  isPriceLoading={isPricesLoading}
                  onClick={token.symbol === 'BBT'
                    ? onOpenBbtInfoModal
                    : token.symbol === 'BBG'
                      ? onOpenBbgInfoModal
                      : token.symbol === 'GGMOMOMO'
                        ? onOpenGgmomomoInfoModal
                        : token.symbol === 'WLD' ? onOpenWldInfoModal : undefined}
                  onClickAriaLabel={token.symbol === 'BBT'
                    ? t('bbt.openInfo')
                    : token.symbol === 'BBG'
                      ? t('bbt.openBbgInfo')
                      : token.symbol === 'GGMOMOMO'
                        ? t('ggmomomo.openInfo')
                        : token.symbol === 'WLD' ? t('wld.openInfo') : undefined}
                />
              ))}
            </div>
          </section>
        </>
      )}

      {view === 'in-game-wallet' && (
        <section className="section-block game-assets-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">IN-GAME ASSETS</p>
              <h2>{t('overview.inGameAssets')}</h2>
            </div>
            <button
              className="text-button"
              type="button"
              onClick={onRefreshGameBalances}
              disabled={isGameBalancesLoading}
            >
              {isGameBalancesLoading ? t('common.loading') : t('overview.updateBalance')}
            </button>
          </div>
          <div className="asset-list game-asset-list">
            {gameAssetCards.map((asset) => (
              <div className="game-asset-card-group" key={asset.symbol}>
                <GameAssetRow
                  {...asset}
                  balance={asset.symbol === 'BBG' ? bbgGameBalance : fcashGameBalance}
                  isLoading={isGameBalancesLoading}
                  error={asset.symbol === 'BBG' ? bbgGameBalanceError : fcashGameBalanceError}
                  onClick={asset.symbol === 'BBG'
                    ? onOpenBubbleGameUniverseModal
                    : asset.symbol === 'F-CASH' ? onOpenFlight404InfoModal : undefined}
                  onClickAriaLabel={asset.symbol === 'BBG'
                    ? t('bubbleGame.openInfo')
                    : asset.symbol === 'F-CASH' ? t('flight404.openInfo') : undefined}
                />
                {asset.symbol === 'BBG' && (
                  <div className="game-asset-transfer-actions">
                    <button type="button" onClick={() => onOpenBbgTransfer('deposit')}>
                      {t('transfer.deposit')}
                    </button>
                    <button type="button" onClick={() => onOpenBbgTransfer('withdraw')}>
                      {t('transfer.withdraw')}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          <PendingWithdrawals
            withdrawals={pendingWithdrawals}
            cancellingId={cancellingWithdrawalId}
            onCancel={onCancelWithdrawal}
          />
          <section className="bubble-game-showcase">
            {bubbleGameLink ? (
              <a className="bubble-game-showcase-link" href={bubbleGameLink} aria-label={t('bubbleGame.enterGame')}>
                <video
                  className="bubble-game-showcase-video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                >
                  <source src="/mp4/bbgame.mp4" type="video/mp4" />
                </video>
              </a>
            ) : (
              <video
                className="bubble-game-showcase-video"
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
              >
                <source src="/mp4/bbgame.mp4" type="video/mp4" />
              </video>
            )}
          </section>
        </section>
      )}
    </>
  );
}
type ActivitySourceState = {
  records: OnChainTransactionRecord[];
  pendingRecords: OnChainTransactionRecord[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMoreError: string | null;
  nextCursor: OnChainTransactionHistoryCursor | null;
};

type BubbleWalletActivityState = {
  records: BubbleWalletActivityRecord[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMoreError: string | null;
};

type ActivitySource = WalletBalanceSource | 'bubble-wallet';

type ActivityState = {
  'bubble-wallet': BubbleWalletActivityState;
  'world-app': ActivitySourceState;
  metamask: ActivitySourceState;
};

const emptyActivitySourceState = (): ActivitySourceState => ({
  records: [],
  pendingRecords: [],
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  error: null,
  loadMoreError: null,
  nextCursor: null,
});

const emptyBubbleWalletActivityState = (): BubbleWalletActivityState => ({
  records: [],
  isLoading: false,
  isLoadingMore: false,
  hasMore: false,
  error: null,
  loadMoreError: null,
});

const ACTIVITY_PAGE_SIZE = 30;
const BUBBLE_WALLET_ACTIVITY_FETCH_SIZE = ACTIVITY_PAGE_SIZE + 1;
const ACTIVITY_LOAD_MORE_PULL_DISTANCE = 64;
const ACTIVITY_LOAD_MORE_MAX_VISUAL_PULL_DISTANCE = 68;
const ACTIVITY_LOAD_MORE_RESISTANCE_FACTOR = 92;
const ACTIVITY_LOAD_MORE_LOADING_VISUAL_PULL_DISTANCE = 40;
const ACTIVITY_LOAD_MORE_SETTLE_DURATION_MS = 280;
const ACTIVITY_DOCUMENT_BOTTOM_TOLERANCE = 2;
const ACTIVITY_WHEEL_GESTURE_IDLE_MS = 250;

type ActivityPullPhase = 'idle' | 'pulling' | 'armed' | 'loading' | 'settling';

function formatBubbleActivityAmount(value: string | null): string {
  if (!value) return '—';
  const normalized = value.trim();
  if (/^-?\d+(?:\.\d+)?$/.test(normalized)) {
    const [integerPart, fractionPart = ''] = normalized.split('.');
    const trimmedFraction = fractionPart.slice(0, 6).replace(/0+$/, '');
    if (trimmedFraction) return `${integerPart}.${trimmedFraction}`;
    const numericValue = Number(normalized);
    return numericValue !== 0 && integerPart === '0'
      ? numericValue.toExponential(4)
      : integerPart;
  }

  const numericValue = Number(normalized);
  if (!Number.isFinite(numericValue)) return '—';
  if (numericValue !== 0 && Math.abs(numericValue) < 0.00000001) return numericValue.toExponential(4);
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

function getActivityMetadataText(record: BubbleWalletActivityRecord, key: string): string | null {
  const value = record.metadata[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function formatActivityDate(value: number | string | null): string {
  if (value === null) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function formatTransactionHash(hash: string | null | undefined): string | null {
  const normalized = hash?.trim();
  if (!normalized) return null;
  if (normalized.length <= 14) return normalized;
  return `${normalized.slice(0, 6)}…${normalized.slice(-6)}`;
}

type ActivityStatusKey = 'completed' | 'failed' | 'pending' | 'unknown';

function getActivityStatusKey(status: string): ActivityStatusKey {
  const normalized = status.toLowerCase();
  if (normalized === 'completed' || normalized === 'confirmed') return 'completed';
  if (normalized === 'failed') return 'failed';
  if (normalized === 'submitted' || normalized === 'pending') return 'pending';
  return 'unknown';
}

function ActivityRecordShell({
  href,
  className,
  children,
}: {
  href?: string;
  className: string;
  children: ReactNode;
}) {
  if (!href) return <div className={className}>{children}</div>;

  return (
    <a className={className} href={href} rel="noreferrer" target="_blank">
      {children}
    </a>
  );
}

function ActivityRecordIcon({
  logo,
  alt,
  direction,
  fallback,
}: {
  logo?: string;
  alt: string;
  direction: 'in' | 'out';
  fallback: 'claim' | 'send' | 'receive';
}) {
  return (
    <span className={`activity-record-icon is-${direction}`} aria-hidden={logo ? undefined : true}>
      {logo ? <img className="activity-token-logo" src={logo} alt={alt} /> : <SummaryIcon name={fallback} />}
    </span>
  );
}

function StandardActivityCard({
  title,
  description,
  amount,
  direction,
  timestamp,
  status,
  statusKey,
  transactionHash,
  icon,
  iconAlt,
  fallbackIcon,
  href,
}: {
  title: string;
  description: string;
  amount: string;
  direction: 'in' | 'out';
  timestamp: number | string | null;
  status: string;
  statusKey: ActivityStatusKey;
  transactionHash?: string | null;
  icon?: string;
  iconAlt: string;
  fallbackIcon: 'claim' | 'send' | 'receive';
  href?: string;
}) {
  const { t } = useTranslation();
  const displayHash = formatTransactionHash(transactionHash);

  return (
    <ActivityRecordShell href={href} className="activity-record activity-standard-record">
      <ActivityRecordIcon logo={icon} alt={iconAlt} direction={direction} fallback={fallbackIcon} />
      <strong className="activity-record-title" title={title}>{title}</strong>
      <strong className={`activity-record-amount activity-amount is-${direction}`} title={amount}>{amount}</strong>
      <span className="activity-record-description" title={description}>{description}</span>
      {displayHash && (
        <span className="activity-record-hash" title={transactionHash ?? undefined}>
          {t('activity.transactionHash')} {displayHash}
        </span>
      )}
      <time className="activity-record-date" dateTime={typeof timestamp === 'string' ? timestamp : undefined}>
        {formatActivityDate(timestamp)}
      </time>
      <span className={`activity-record-status is-${statusKey}`}>
        {status}
      </span>
    </ActivityRecordShell>
  );
}

function SwapActivityCard({
  source,
  fromAmount,
  fromToken,
  toAmount,
  toToken,
  fromLogo,
  toLogo,
  timestamp,
  status,
  statusKey,
  transactionHash,
  href,
}: {
  source: string;
  fromAmount: string | null;
  fromToken: string | null;
  toAmount: string | null;
  toToken: string | null;
  fromLogo?: string;
  toLogo?: string;
  timestamp: number | string | null;
  status: string;
  statusKey: ActivityStatusKey;
  transactionHash?: string | null;
  href?: string;
}) {
  const { t } = useTranslation();
  const displayHash = formatTransactionHash(transactionHash);
  const displayFromToken = fromToken ?? t('activity.unknownToken');
  const displayToToken = toToken ?? t('activity.unknownToken');
  const hasSettledOutput = statusKey === 'completed' && Boolean(toAmount);

  return (
    <ActivityRecordShell href={href} className="activity-record activity-swap-record">
      <span className="activity-swap-icon" aria-hidden="true">
        {fromLogo && <img className="activity-swap-logo" src={fromLogo} alt="" />}
        {toLogo && <img className="activity-swap-logo" src={toLogo} alt="" />}
        {!fromLogo && !toLogo && <SummaryIcon name="swap" />}
      </span>
      <strong className="activity-swap-title">{t('activity.tokenSwap')}</strong>
      <span className={`activity-record-status is-${statusKey}`}>{status}</span>
      <span className="activity-swap-source">{source}</span>
      <div className="activity-swap-pair" aria-label={`${fromAmount ?? '—'} ${displayFromToken} ${t('activity.swapSent')} ${hasSettledOutput ? `${toAmount} ${displayToToken}` : '—'}`}>
        <span className="activity-swap-leg is-out">
          <strong>{fromAmount ? `-${fromAmount}` : '—'} {displayFromToken}</strong>
          <small>{t('activity.swapSent')}</small>
        </span>
        <span className="activity-swap-arrow" aria-hidden="true">→</span>
        <span className="activity-swap-leg is-in">
          <strong>{hasSettledOutput ? `+${toAmount} ${displayToToken}` : `— ${displayToToken}`}</strong>
          <small>{t('activity.swapReceived')}</small>
        </span>
      </div>
      <time className="activity-record-date" dateTime={typeof timestamp === 'string' ? timestamp : undefined}>
        {formatActivityDate(timestamp)}
      </time>
      {displayHash && (
        <span className="activity-swap-hash" title={transactionHash ?? undefined}>
          {t('activity.transactionHash')} {displayHash}
        </span>
      )}
    </ActivityRecordShell>
  );
}

export function ActivityLoadMore({
  recordCount,
  hasMore,
  isLoadingMore,
  error,
  onLoadMore,
  children,
}: {
  recordCount: number;
  hasMore: boolean;
  isLoadingMore: boolean;
  error: string | null;
  onLoadMore: () => void | Promise<unknown>;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  const touchStartYRef = useRef<number | null>(null);
  const touchArmedRef = useRef(false);
  const touchPullDistanceRef = useRef(0);
  const loadTriggeredRef = useRef(false);
  const wheelPullDistanceRef = useRef(0);
  const wheelGestureActiveRef = useRef(false);
  const wheelStartedAtBottomRef = useRef(false);
  const wheelGestureResetTimeoutRef = useRef<number | null>(null);
  const settleTimeoutRef = useRef<number | null>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  const pullPhaseRef = useRef<ActivityPullPhase>('idle');
  const [pullPresentation, setPullPresentation] = useState({ phase: 'idle' as ActivityPullPhase, distance: 0 });
  onLoadMoreRef.current = onLoadMore;

  const setPullState = useCallback((phase: ActivityPullPhase, distance: number) => {
    pullPhaseRef.current = phase;
    setPullPresentation({ phase, distance });
  }, []);

  const clearSettleTimeout = useCallback(() => {
    if (settleTimeoutRef.current !== null) {
      window.clearTimeout(settleTimeoutRef.current);
      settleTimeoutRef.current = null;
    }
  }, []);

  const resetTouchGesture = useCallback(() => {
    touchStartYRef.current = null;
    touchArmedRef.current = false;
    touchPullDistanceRef.current = 0;
  }, []);

  const resetWheelGesture = useCallback(() => {
    if (wheelGestureResetTimeoutRef.current !== null) {
      window.clearTimeout(wheelGestureResetTimeoutRef.current);
      wheelGestureResetTimeoutRef.current = null;
    }
    wheelPullDistanceRef.current = 0;
    wheelGestureActiveRef.current = false;
    wheelStartedAtBottomRef.current = false;
  }, []);

  const settlePull = useCallback(() => {
    clearSettleTimeout();
    if (pullPhaseRef.current === 'idle') return;
    setPullState('settling', 0);
    settleTimeoutRef.current = window.setTimeout(() => {
      settleTimeoutRef.current = null;
      loadTriggeredRef.current = false;
      setPullState('idle', 0);
    }, ACTIVITY_LOAD_MORE_SETTLE_DURATION_MS);
  }, [clearSettleTimeout, setPullState]);

  const triggerLoadMore = useCallback(() => {
    if (loadTriggeredRef.current || !hasMore || isLoadingMore || error) return;
    loadTriggeredRef.current = true;
    resetTouchGesture();
    resetWheelGesture();
    clearSettleTimeout();
    setPullState('loading', ACTIVITY_LOAD_MORE_LOADING_VISUAL_PULL_DISTANCE);
    void Promise.resolve(onLoadMoreRef.current()).finally(settlePull);
  }, [clearSettleTimeout, error, hasMore, isLoadingMore, resetTouchGesture, resetWheelGesture, setPullState, settlePull]);

  useEffect(() => {
    if (!hasMore || isLoadingMore || error) return undefined;

    const isAtDocumentBottom = () => {
      const scrollingElement = document.scrollingElement ?? document.documentElement;
      return scrollingElement.scrollTop + scrollingElement.clientHeight
        >= scrollingElement.scrollHeight - ACTIVITY_DOCUMENT_BOTTOM_TOLERANCE;
    };

    const scheduleWheelGestureReset = () => {
      if (wheelGestureResetTimeoutRef.current !== null) {
        window.clearTimeout(wheelGestureResetTimeoutRef.current);
      }
      wheelGestureResetTimeoutRef.current = window.setTimeout(() => {
        wheelGestureResetTimeoutRef.current = null;
        wheelPullDistanceRef.current = 0;
        wheelGestureActiveRef.current = false;
        wheelStartedAtBottomRef.current = false;
      }, ACTIVITY_WHEEL_GESTURE_IDLE_MS);
    };

    const handleTouchStart = (event: TouchEvent) => {
      const touch = event.touches[0];
      resetTouchGesture();
      if (!touch) return;
      if (pullPhaseRef.current !== 'idle' || !isAtDocumentBottom()) return;

      touchStartYRef.current = touch.clientY;
      touchArmedRef.current = true;
      setPullState('pulling', 0);
    };

    const handleTouchMove = (event: TouchEvent) => {
      const touch = event.touches[0];
      const startY = touchStartYRef.current;
      if (!touch || startY === null || !touchArmedRef.current || loadTriggeredRef.current) return;

      if (!isAtDocumentBottom()) {
        resetTouchGesture();
        settlePull();
        return;
      }

      const rawPullDistance = Math.max(0, startY - touch.clientY);
      touchPullDistanceRef.current = rawPullDistance;
      const visualPullDistance = ACTIVITY_LOAD_MORE_MAX_VISUAL_PULL_DISTANCE
        * (1 - Math.exp(-rawPullDistance / ACTIVITY_LOAD_MORE_RESISTANCE_FACTOR));
      setPullState(
        rawPullDistance >= ACTIVITY_LOAD_MORE_PULL_DISTANCE ? 'armed' : 'pulling',
        visualPullDistance,
      );
    };

    const handleTouchEnd = () => {
      const shouldLoad = touchArmedRef.current
        && touchPullDistanceRef.current >= ACTIVITY_LOAD_MORE_PULL_DISTANCE
        && pullPhaseRef.current === 'armed';
      resetTouchGesture();
      if (shouldLoad) {
        triggerLoadMore();
        return;
      }
      settlePull();
    };

    const handleTouchCancel = () => {
      resetTouchGesture();
      settlePull();
    };

    const handleWheel = (event: WheelEvent) => {
      scheduleWheelGestureReset();
      const isAtBottom = isAtDocumentBottom();
      if (!wheelGestureActiveRef.current) {
        wheelGestureActiveRef.current = true;
        wheelStartedAtBottomRef.current = isAtBottom;
      }

      if (!wheelStartedAtBottomRef.current || !isAtBottom || event.deltaY <= 0) {
        wheelPullDistanceRef.current = 0;
        if (!isAtBottom || event.deltaY <= 0) wheelStartedAtBottomRef.current = false;
        return;
      }

      if (loadTriggeredRef.current || pullPhaseRef.current !== 'idle') {
        return;
      }

      const scrollingElement = document.scrollingElement ?? document.documentElement;
      const wheelDeltaPixels = event.deltaMode === 1
        ? event.deltaY * 16
        : event.deltaMode === 2
          ? event.deltaY * scrollingElement.clientHeight
          : event.deltaY;
      wheelPullDistanceRef.current += wheelDeltaPixels;
      if (wheelPullDistanceRef.current >= ACTIVITY_LOAD_MORE_PULL_DISTANCE) {
        triggerLoadMore();
      }
    };

    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    window.addEventListener('touchcancel', handleTouchCancel, { passive: true });
    window.addEventListener('wheel', handleWheel, { passive: true });
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      window.removeEventListener('touchcancel', handleTouchCancel);
      window.removeEventListener('wheel', handleWheel);
      resetTouchGesture();
      resetWheelGesture();
    };
  }, [error, hasMore, isLoadingMore, resetTouchGesture, resetWheelGesture, setPullState, settlePull, triggerLoadMore]);

  useEffect(() => {
    if (isLoadingMore) {
      clearSettleTimeout();
      setPullState('loading', ACTIVITY_LOAD_MORE_LOADING_VISUAL_PULL_DISTANCE);
      return;
    }
    if (pullPhaseRef.current === 'loading') settlePull();
  }, [clearSettleTimeout, isLoadingMore, setPullState, settlePull]);

  useEffect(() => () => {
    clearSettleTimeout();
  }, [clearSettleTimeout]);

  if (!error && recordCount === 0 && !hasMore) return <>{children}</>;

  const isLoading = pullPresentation.phase === 'loading' || isLoadingMore;
  const pullStyle = {
    '--activity-pull-offset': `${pullPresentation.distance}px`,
    '--activity-pull-translate': `-${pullPresentation.distance}px`,
    '--activity-loader-translate': `-${Math.min(36, pullPresentation.distance)}px`,
    '--activity-pull-progress': String(Math.min(1, pullPresentation.distance / ACTIVITY_LOAD_MORE_LOADING_VISUAL_PULL_DISTANCE)),
  } as CSSProperties;

  return (
    <div
      className={`activity-pull-region is-${pullPresentation.phase}`}
      data-pull-phase={pullPresentation.phase}
      style={pullStyle}
    >
      <div className="activity-pull-content">{children}</div>
      <div className={`activity-load-more${error ? ' activity-load-more-error' : ''}`} aria-live={isLoading ? 'polite' : 'off'}>
        {error ? (
          <>
            <p className="send-token-error" role="alert">{error}</p>
            <button className="activity-load-more-button" type="button" onClick={() => void onLoadMore()}>
              {t('activity.loadMore')}
            </button>
          </>
        ) : isLoading ? (
          <span
            className="activity-load-more-spinner"
            role="status"
            aria-label={t('activity.loadingMore')}
          />
        ) : hasMore ? (
          <>
            <span className="activity-load-more-preview" aria-hidden="true" />
            <span className="activity-load-more-hint">{t('activity.pullUpToLoad')}</span>
          </>
        ) : t('activity.noMoreRecords')}
      </div>
    </div>
  );
}

function ActivityRecordList({
  records,
  source,
}: {
  records: OnChainTransactionRecord[];
  source: WalletBalanceSource;
}) {
  const { t } = useTranslation();
  if (records.length === 0) {
    return <p className="activity-empty-copy">{t('activity.noTransactions')}</p>;
  }

  return (
    <div className="activity-record-list">
      {records.map((record) => {
        const tokenLogo = tokenCards.find((token) => token.symbol === record.tokenSymbol)?.logo;
        const fromToken = record.swapFromToken ?? record.tokenSymbol;
        const toToken = record.swapToToken ?? record.tokenSymbol;
        const fromTokenLogo = tokenCards.find((token) => token.symbol === fromToken)?.logo;
        const toTokenLogo = tokenCards.find((token) => token.symbol === toToken)?.logo;
        const isSwap = record.kind === 'swap';

        return isSwap ? (
          <SwapActivityCard
            fromAmount={record.swapFromAmount ? formatBubbleActivityAmount(record.swapFromAmount) : null}
            fromLogo={fromTokenLogo}
            fromToken={fromToken}
            href={`https://worldscan.org/tx/${record.hash}`}
            key={`${record.hash}-${record.kind}-${record.tokenSymbol}`}
            status={t('activity.confirmed')}
            statusKey="completed"
            timestamp={record.timestamp}
            transactionHash={record.hash}
            toAmount={record.swapToAmount ? formatBubbleActivityAmount(record.swapToAmount) : null}
            toLogo={toTokenLogo}
            toToken={toToken}
            source={source === 'metamask' ? 'METAMASK' : 'WORLD APP'}
          />
        ) : (
          <StandardActivityCard
            amount={`${record.direction === 'in' ? '+' : '-'}${formatBubbleActivityAmount(record.amount)} ${record.tokenSymbol}`}
            direction={record.direction}
            fallbackIcon={record.direction === 'in' ? 'receive' : 'send'}
            href={`https://worldscan.org/tx/${record.hash}`}
            icon={tokenLogo}
            iconAlt={`${record.tokenSymbol} logo`}
            key={`${record.hash}-${record.kind}-${record.tokenSymbol}`}
            status={t('activity.confirmed')}
            statusKey="completed"
            timestamp={record.timestamp}
            transactionHash={record.hash}
            title={record.direction === 'in' ? t('activity.receivedToken') : t('activity.sentToken')}
            description={record.tokenSymbol}
          />
        );
      })}
    </div>
  );
}

function BubbleWalletActivityList({ records }: { records: BubbleWalletActivityRecord[] }) {
  const { t } = useTranslation();

  if (records.length === 0) {
    return <p className="activity-empty-copy">{t('activity.bubbleWallet.noRecords')}</p>;
  }

  return (
    <div className="activity-record-list">
      {records.map((record) => {
        const itemName = getActivityMetadataText(record, 'item_display_name')
          ?? getActivityMetadataText(record, 'asset_display_name');
        const title = itemName ?? t(`activity.bubbleWallet.types.${record.activityType}`);
        const statusKey = getActivityStatusKey(record.status);
        const isSwap = record.activityType === 'swap';
        const tokenLogo = record.tokenSymbol
          ? tokenCards.find((token) => token.symbol === record.tokenSymbol)?.logo
          : undefined;
        const outputTokenLogo = record.tokenSymbolOut
          ? tokenCards.find((token) => token.symbol === record.tokenSymbolOut)?.logo
          : undefined;
        const hasTransactionHash = /^0x[\da-f]{64}$/i.test(record.txHash ?? '');
        const outputToken = record.tokenSymbolOut
          ?? getActivityMetadataText(record, 'token_out')
          ?? getActivityMetadataText(record, 'output_token_symbol')
          ?? record.tokenSymbol
          ?? t('activity.unknownToken');
        const inputToken = record.tokenSymbol ?? t('activity.unknownToken');
        const description = itemName
          ? `${record.activityDomain === 'bubble_game' ? 'BUBBLE GAME' : 'BUBBLE WALLET'} · ${t(`activity.bubbleWallet.types.${record.activityType}`)}`
          : record.activityDomain === 'bubble_game' ? 'BUBBLE GAME' : 'BUBBLE WALLET';
        const direction = record.direction === 'receive' ? 'in' : 'out';
        const href = hasTransactionHash ? `https://worldscan.org/tx/${record.txHash}` : undefined;

        if (isSwap) {
          return (
            <SwapActivityCard
              fromAmount={record.amount ? formatBubbleActivityAmount(record.amount) : null}
              fromLogo={tokenLogo}
              fromToken={inputToken}
              href={href}
              key={record.activityId}
              status={t(`activity.bubbleWallet.statuses.${statusKey}`)}
              statusKey={statusKey}
              timestamp={record.occurredAt}
              transactionHash={record.txHash}
              toAmount={record.amountOut ? formatBubbleActivityAmount(record.amountOut) : null}
              toLogo={outputTokenLogo}
              toToken={outputToken}
              source={record.activityDomain === 'bubble_game' ? 'BUBBLE GAME' : 'BUBBLE WALLET'}
            />
          );
        }

        return (
          <StandardActivityCard
            amount={record.amount
              ? `${record.direction === 'receive' ? '+' : '-'}${formatBubbleActivityAmount(record.amount)} ${inputToken}`
              : `${t('activity.bubbleWallet.recorded')} · ${inputToken}`}
            direction={direction}
            fallbackIcon={record.activityType === 'claim_bbg' ? 'claim' : direction === 'in' ? 'receive' : 'send'}
            href={href}
            icon={tokenLogo}
            iconAlt={`${record.tokenSymbol ?? 'Activity'} logo`}
            key={record.activityId}
            status={t(`activity.bubbleWallet.statuses.${statusKey}`)}
            statusKey={statusKey}
            timestamp={record.occurredAt}
            transactionHash={record.txHash}
            title={title}
            description={description}
          />
        );
      })}
    </div>
  );
}

function BubbleWalletActivityPanel({
  state,
  isAuthenticated,
  onLoadMore,
}: {
  state: BubbleWalletActivityState;
  isAuthenticated: boolean;
  onLoadMore: () => void | Promise<unknown>;
}) {
  const { t } = useTranslation();

  return (
    <section className="activity-wallet-panel bubble-wallet-activity-panel">
      <div className="activity-panel-heading">
        <div className="activity-source-identity">
          <span className="activity-bubble-icon" aria-hidden="true">
            <img src="/image/bubblewallet_logo_05.png" alt="" />
          </span>
          <div>
            <p className="eyebrow">BUBBLE WALLET</p>
            <h3>{t('activity.bubbleWallet.title')}</h3>
          </div>
        </div>
      </div>

      {!isAuthenticated ? (
        <p className="activity-empty-copy">{t('activity.bubbleWallet.loginRequired')}</p>
      ) : state.isLoading ? (
        <p className="activity-empty-copy">{t('activity.bubbleWallet.loading')}</p>
      ) : state.error ? (
        <p className="send-token-error" role="alert">{state.error}</p>
      ) : (
        <ActivityLoadMore
          error={state.loadMoreError}
          hasMore={state.hasMore}
          isLoadingMore={state.isLoadingMore}
          onLoadMore={onLoadMore}
          recordCount={state.records.length}
        >
          <BubbleWalletActivityList records={state.records} />
        </ActivityLoadMore>
      )}
    </section>
  );
}

function ActivityWalletPanel({
  source,
  address,
  state,
  onLoadMore,
  onConnectMetaMask,
  isMetaMaskLoading,
  metaMaskError,
}: {
  source: WalletBalanceSource;
  address: string | null;
  state: ActivitySourceState;
  onLoadMore: () => void | Promise<unknown>;
  onConnectMetaMask: () => Promise<void>;
  isMetaMaskLoading: boolean;
  metaMaskError: string | null;
}) {
  const { t } = useTranslation();
  const isMetaMask = source === 'metamask';
  const label = t('activity.onChainRecords');

  return (
    <section className="activity-wallet-panel">
      <div className="activity-panel-heading">
        <div className="activity-source-identity">
          <WalletSourceIcon source={source} />
          <div>
            <p className="eyebrow">{isMetaMask ? 'METAMASK WALLET' : 'WORLD APP WALLET'}</p>
            <h3>{label}</h3>
          </div>
        </div>
        {address && <code>{formatWalletAddress(address)}</code>}
      </div>

      {!address && isMetaMask ? (
        <div className="activity-wallet-empty">
          <p>{t('activity.connectMetaMaskDescription')}</p>
          {metaMaskError && <p className="send-token-error" role="alert">{metaMaskError}</p>}
          <button className="auth-button" type="button" onClick={() => void onConnectMetaMask()} disabled={isMetaMaskLoading}>
            {isMetaMaskLoading ? t('common.connect') : t('common.connectMetaMask')}
          </button>
        </div>
      ) : !address ? (
        <p className="activity-empty-copy">{t('activity.loggedInDescription')}</p>
      ) : state.isLoading ? (
        <p className="activity-empty-copy">{t('activity.loadingRecords')}</p>
      ) : state.error ? (
        <p className="send-token-error" role="alert">{state.error}</p>
      ) : (
        <ActivityLoadMore
          error={state.loadMoreError}
          hasMore={state.hasMore}
          isLoadingMore={state.isLoadingMore}
          onLoadMore={onLoadMore}
          recordCount={state.records.length}
        >
          <ActivityRecordList records={state.records} source={source} />
        </ActivityLoadMore>
      )}
    </section>
  );
}

function Activity({
  worldAppAddress,
  metaMaskAddress,
  activityState,
  isAuthenticated,
  onLoadMoreActivity,
  selectedSource,
  onSelectSource,
  onConnectMetaMask,
  isMetaMaskLoading,
  metaMaskError,
}: {
  worldAppAddress: string | null;
  metaMaskAddress: string | null;
  activityState: ActivityState;
  isAuthenticated: boolean;
  onLoadMoreActivity: (source: ActivitySource) => void | Promise<unknown>;
  selectedSource: ActivitySource;
  onSelectSource: (source: ActivitySource) => void;
  onConnectMetaMask: () => Promise<void>;
  isMetaMaskLoading: boolean;
  metaMaskError: string | null;
}) {
  const { t } = useTranslation();

  return (
    <section className="activity-page">
      <div className="activity-source-tabs" role="tablist" aria-label={t('activity.selectTransactionWallet')}>
        <button
          className={`activity-source-tag is-bubble-wallet${selectedSource === 'bubble-wallet' ? ' is-active' : ''}`}
          type="button"
          role="tab"
          aria-selected={selectedSource === 'bubble-wallet'}
          onClick={() => onSelectSource('bubble-wallet')}
        >
          <span className="activity-bubble-tab-icon" aria-hidden="true">
            <img src="/image/bubblewallet_logo_05.png" alt="" />
          </span>
          <span className="activity-source-tag-label">BUBBLE WALLET</span>
        </button>
        <button
          className={`activity-source-tag is-world-app${selectedSource === 'world-app' ? ' is-active' : ''}`}
          type="button"
          role="tab"
          aria-selected={selectedSource === 'world-app'}
          onClick={() => onSelectSource('world-app')}
        >
          <WalletSourceIcon source="world-app" />
          <span className="activity-source-tag-label">WORLD APP</span>
        </button>
        <button
          className={`activity-source-tag is-metamask${selectedSource === 'metamask' ? ' is-active' : ''}`}
          type="button"
          role="tab"
          aria-selected={selectedSource === 'metamask'}
          onClick={() => onSelectSource('metamask')}
        >
          <WalletSourceIcon source="metamask" />
          <span className="activity-source-tag-label">METAMASK</span>
        </button>
      </div>
      <div className="activity-wallet-list">
        {selectedSource === 'bubble-wallet' ? (
          <BubbleWalletActivityPanel
            state={activityState['bubble-wallet']}
            isAuthenticated={isAuthenticated}
            onLoadMore={() => onLoadMoreActivity('bubble-wallet')}
          />
        ) : (
          <ActivityWalletPanel
            source={selectedSource}
            address={selectedSource === 'metamask' ? metaMaskAddress : worldAppAddress}
            state={activityState[selectedSource]}
            onLoadMore={() => onLoadMoreActivity(selectedSource)}
            onConnectMetaMask={onConnectMetaMask}
            isMetaMaskLoading={isMetaMaskLoading}
            metaMaskError={metaMaskError}
          />
        )}
      </div>
    </section>
  );
}

function Settings({
  walletAddress,
  metaMaskAddress,
  isMetaMaskLoading,
  isMetaMaskDisconnecting,
  metaMaskError,
  onConnectMetaMask,
  onDisconnectMetaMask,
}: {
  walletAddress: string | null;
  metaMaskAddress: string | null;
  isMetaMaskLoading: boolean;
  isMetaMaskDisconnecting: boolean;
  metaMaskError: string | null;
  onConnectMetaMask: () => Promise<void>;
  onDisconnectMetaMask: () => Promise<void>;
}) {
  const { session, profile, signOut } = useAuth();
  const { t, i18n } = useTranslation();
  const selectedLanguage = i18n.resolvedLanguage === 'zh-Hant' ? 'zh-Hant' : 'en';

  return (
    <section className="page-card settings-page">
      <div className="settings-account-section">
        <div className="settings-section-heading">
          <p className="eyebrow">ACCOUNT IDENTITY</p>
          <h3>{t('settings.accountIdentity')}</h3>
        </div>
        <div className="wallet-connection-card settings-account-card">
          <div className="wallet-connection-identity">
            <span className="settings-account-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="8" r="3.2" />
                <path d="M5.5 20c.8-3.3 3-5 6.5-5s5.7 1.7 6.5 5" />
              </svg>
            </span>
            <div>
              <strong>{t('settings.account')}</strong>
              <span>{session ? t('settings.signedIn') : t('settings.notSignedIn')}</span>
              {session && <code>{profile?.username ?? 'GGMOMOMO'}</code>}
            </div>
          </div>
          {session && (
            <button className="settings-sign-out" type="button" onClick={() => void signOut()}>
              {t('settings.signOut')}
            </button>
          )}
        </div>
      </div>
      <div className="settings-connection-section">
        <div className="settings-section-heading">
          <p className="eyebrow">WALLET STATUS</p>
          <h3>{t('settings.walletInfo')}</h3>
        </div>
        <div className="settings-wallet-list">
          <div className="wallet-connection-card settings-wallet-card">
            <div className="wallet-connection-identity">
              <WalletSourceIcon source="world-app" />
              <div>
                <strong>{t('settings.worldAppWallet')}</strong>
                <span>{walletAddress ? t('settings.worldChainConnected') : t('common.notConnected')}</span>
                <code>{walletAddress ? formatWalletAddress(walletAddress) : t('settings.noAddress')}</code>
              </div>
            </div>
          </div>
          <div className="wallet-connection-card settings-wallet-card">
            <div className="wallet-connection-identity">
              <WalletSourceIcon source="metamask" />
              <div>
                <strong>{t('settings.metaMaskConnection')}</strong>
                <span>{metaMaskAddress ? t('settings.worldChainConnected') : t('common.notConnected')}</span>
                {metaMaskAddress && <code>{formatWalletAddress(metaMaskAddress)}</code>}
              </div>
            </div>
            {metaMaskAddress ? (
              <button
                className="settings-disconnect"
                type="button"
                onClick={() => void onDisconnectMetaMask()}
                disabled={isMetaMaskDisconnecting}
              >
                {isMetaMaskDisconnecting ? t('settings.disconnecting') : t('settings.disconnectMetaMask')}
              </button>
            ) : (
              <button
                className="auth-button secondary"
                type="button"
                onClick={() => void onConnectMetaMask()}
                disabled={isMetaMaskLoading}
              >
                {isMetaMaskLoading ? t('common.connect') : t('common.connectMetaMask')}
              </button>
            )}
          </div>
        </div>
        {metaMaskError && <p className="send-token-error" role="alert">{metaMaskError}</p>}
      </div>
      <div className="settings-language-section">
        <div className="settings-section-heading">
          <p className="eyebrow">LANGUAGE</p>
          <h3>{t('settings.language')}</h3>
          <p className="settings-help-text">{t('settings.languageDescription')}</p>
        </div>
        <label className="settings-language-field">
          <span className="sr-only">{t('settings.language')}</span>
          <select
            aria-label={t('settings.language')}
            value={selectedLanguage}
            onChange={(event) => changeAppLanguage(event.target.value as AppLanguage)}
          >
            {languageOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>
      <div className="settings-legal-section">
        <div className="settings-section-heading">
          <p className="eyebrow">{t('settings.legal')}</p>
          <h3>{t('settings.legalDocuments')}</h3>
        </div>
        <div className="settings-legal-links">
          <a className="settings-legal-link" href="/privacy">
            <span>{t('settings.privacyPolicy')}</span>
            <span aria-hidden="true">↗</span>
          </a>
          <a className="settings-legal-link" href="/terms">
            <span>{t('settings.termsOfUse')}</span>
            <span aria-hidden="true">↗</span>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function App() {
  const [activeTab, setActiveTab] = useState<WalletTab>('in-game-wallet');
  const { t } = useTranslation();
  const {
    isWorldApp,
    session,
    profile,
    isLoading: isAuthLoading,
    isSigningIn,
    error,
    signIn,
  } = useAuth();
  const [balanceSource, setBalanceSource] = useState<WalletBalanceSource>('world-app');
  const [onChainBalances, setOnChainBalances] = useState<
    Partial<Record<OnChainTokenSymbol, OnChainTokenBalance>>
  >({});
  const [isBalancesLoading, setIsBalancesLoading] = useState(false);
  const [hasOnChainBalancesLoaded, setHasOnChainBalancesLoaded] = useState(false);
  const [metaMaskAddress, setMetaMaskAddress] = useState<string | null>(null);
  const [metaMaskBalances, setMetaMaskBalances] = useState<
    Partial<Record<OnChainTokenSymbol, OnChainTokenBalance>>
  >({});
  const [isMetaMaskBalancesLoading, setIsMetaMaskBalancesLoading] = useState(false);
  const [hasMetaMaskBalancesLoaded, setHasMetaMaskBalancesLoaded] = useState(false);
  const [isMetaMaskDisconnecting, setIsMetaMaskDisconnecting] = useState(false);
  const [metaMaskBalanceError, setMetaMaskBalanceError] = useState<string | null>(null);
  const [activitySource, setActivitySource] = useState<ActivitySource>('bubble-wallet');
  const [activityState, setActivityState] = useState<ActivityState>({
    'bubble-wallet': emptyBubbleWalletActivityState(),
    'world-app': emptyActivitySourceState(),
    metamask: emptyActivitySourceState(),
  });
  const [tokenPrices, setTokenPrices] = useState<Partial<Record<OnChainTokenSymbol, TokenPrice>>>({});
  const [isTokenPricesLoading, setIsTokenPricesLoading] = useState(false);
  const [tokenPriceError, setTokenPriceError] = useState<string | null>(null);
  const [tokenPricesFetchedAt, setTokenPricesFetchedAt] = useState<number | null>(null);
  const [bbgGameBalance, setBbgGameBalance] = useState<InGameBbgBalance | null>(null);
  const [fcashGameBalance, setFcashGameBalance] = useState<FcashWallet | null>(null);
  const [isGameBalancesLoading, setIsGameBalancesLoading] = useState(false);
  const [bbgGameBalanceError, setBbgGameBalanceError] = useState<string | null>(null);
  const [fcashGameBalanceError, setFcashGameBalanceError] = useState<string | null>(null);
  const [sendTokenSymbol, setSendTokenSymbol] = useState<OnChainTokenSymbol | null>(null);
  const [isSendTokenModalOpen, setIsSendTokenModalOpen] = useState(false);
  const [isSwapModalOpen, setIsSwapModalOpen] = useState(false);
  const [swapPreset, setSwapPreset] = useState<SwapPreset | null>(null);
  const [isBbtInfoModalOpen, setIsBbtInfoModalOpen] = useState(false);
  const [isBbgInfoModalOpen, setIsBbgInfoModalOpen] = useState(false);
  const [isGgmomomoInfoModalOpen, setIsGgmomomoInfoModalOpen] = useState(false);
  const [isWldInfoModalOpen, setIsWldInfoModalOpen] = useState(false);
  const [isBubbleGameUniverseModalOpen, setIsBubbleGameUniverseModalOpen] = useState(false);
  const [isFlight404InfoModalOpen, setIsFlight404InfoModalOpen] = useState(false);
  const [isReceiveAddressModalOpen, setIsReceiveAddressModalOpen] = useState(false);
  const [isClaimModalOpen, setIsClaimModalOpen] = useState(false);
  const [bbgTransferMode, setBbgTransferMode] = useState<BbgTransferMode | null>(null);
  const [pendingWithdrawals, setPendingWithdrawals] = useState<PendingWithdrawal[]>([]);
  const [cancellingWithdrawalId, setCancellingWithdrawalId] = useState<string | null>(null);

  const walletAddress = useMemo(() => {
    const metadataWalletAddress = session?.user.user_metadata.wallet_address;
    return profile?.wallet_address
      ?? (typeof metadataWalletAddress === 'string' ? metadataWalletAddress : null);
  }, [profile?.wallet_address, session?.user.user_metadata.wallet_address]);

  const activityRequestGeneration = useRef(0);
  const activityLoadMoreInFlight = useRef<Record<ActivitySource, boolean>>({
    'bubble-wallet': false,
    'world-app': false,
    metamask: false,
  });
  const sessionRef = useRef(session);
  const walletAddressRef = useRef(walletAddress);
  const metaMaskAddressRef = useRef(metaMaskAddress);
  const onChainBalanceRequestGeneration = useRef(0);
  const metaMaskBalanceRequestGeneration = useRef(0);
  sessionRef.current = session;
  walletAddressRef.current = walletAddress;
  metaMaskAddressRef.current = metaMaskAddress;

  const refreshOnChainBalances = useCallback(async () => {
    const requestGeneration = onChainBalanceRequestGeneration.current + 1;
    onChainBalanceRequestGeneration.current = requestGeneration;
    const requestedAddress = walletAddress;
    const isCurrentRequest = () => (
      onChainBalanceRequestGeneration.current === requestGeneration
      && walletAddressRef.current?.toLowerCase() === requestedAddress?.toLowerCase()
    );

    if (!requestedAddress) {
      if (!isCurrentRequest()) return;
      setOnChainBalances({});
      setIsBalancesLoading(isAuthLoading);
      setHasOnChainBalancesLoaded(!isAuthLoading);
      return;
    }

    setIsBalancesLoading(true);
    try {
      const balances = await getOnChainBalances(requestedAddress);
      if (isCurrentRequest()) setOnChainBalances(balances);
    } finally {
      if (isCurrentRequest()) {
        setIsBalancesLoading(false);
        setHasOnChainBalancesLoaded(true);
      }
    }
  }, [isAuthLoading, walletAddress]);

  const refreshMetaMaskBalances = useCallback(async (address: string | null) => {
    const requestGeneration = metaMaskBalanceRequestGeneration.current + 1;
    metaMaskBalanceRequestGeneration.current = requestGeneration;
    const requestedAddress = address;
    const isCurrentRequest = () => (
      metaMaskBalanceRequestGeneration.current === requestGeneration
      && metaMaskAddressRef.current?.toLowerCase() === requestedAddress?.toLowerCase()
    );

    if (!address) {
      if (!isCurrentRequest()) return;
      setMetaMaskBalances({});
      setIsMetaMaskBalancesLoading(false);
      setHasMetaMaskBalancesLoaded(true);
      return;
    }

    setIsMetaMaskBalancesLoading(true);
    setMetaMaskBalanceError(null);
    try {
      const balances = await getOnChainBalances(address);
      if (isCurrentRequest()) setMetaMaskBalances(balances);
    } catch (refreshError) {
      if (isCurrentRequest()) {
        setMetaMaskBalances({});
        setMetaMaskBalanceError(formatErrorWithCode(refreshError, t, 'errors.metaMaskBalance'));
      }
    } finally {
      if (isCurrentRequest()) {
        setIsMetaMaskBalancesLoading(false);
        setHasMetaMaskBalancesLoaded(true);
      }
    }
  }, [t]);

  const refreshActivity = useCallback(async () => {
    const requestGeneration = activityRequestGeneration.current + 1;
    activityRequestGeneration.current = requestGeneration;
    const requestUserId = session?.user.id ?? null;
    const isCurrentRequest = (source?: WalletBalanceSource, requestedAddress?: string | null) => {
      if (activityRequestGeneration.current !== requestGeneration) return false;
      if ((sessionRef.current?.user.id ?? null) !== requestUserId) return false;
      if (!source) return true;
      const currentAddress = source === 'world-app' ? walletAddressRef.current : metaMaskAddressRef.current;
      return currentAddress === requestedAddress;
    };

    const refreshBubbleWalletActivity = async () => {
      if (!session) {
        if (isCurrentRequest()) {
          setActivityState((current) => ({
            ...current,
            'bubble-wallet': emptyBubbleWalletActivityState(),
          }));
        }
        return;
      }

      if (isCurrentRequest()) {
        setActivityState((current) => ({
          ...current,
          'bubble-wallet': {
            ...emptyBubbleWalletActivityState(),
            isLoading: true,
          },
        }));
      }
      try {
        const fetchedRecords = await getBubbleWalletSpendingActivity(BUBBLE_WALLET_ACTIVITY_FETCH_SIZE);
        if (!isCurrentRequest()) return;
        setActivityState((current) => ({
          ...current,
          'bubble-wallet': {
            records: fetchedRecords.slice(0, ACTIVITY_PAGE_SIZE),
            isLoading: false,
            isLoadingMore: false,
            hasMore: fetchedRecords.length > ACTIVITY_PAGE_SIZE,
            error: null,
            loadMoreError: null,
          },
        }));
      } catch (activityError) {
        if (!isCurrentRequest()) return;
        setActivityState((current) => ({
          ...current,
          'bubble-wallet': {
            ...current['bubble-wallet'],
            isLoading: false,
            isLoadingMore: false,
            hasMore: false,
            error: formatErrorWithCode(activityError, t, 'errors.walletActivity'),
            loadMoreError: null,
          },
        }));
      }
    };

    const refreshOnChainActivity = async (source: WalletBalanceSource, address: string | null) => {
      if (!isCurrentRequest(source, address)) return;
      if (!address) {
        setActivityState((current) => ({
          ...current,
          [source]: emptyActivitySourceState(),
        }));
        return;
      }

      if (!isCurrentRequest(source, address)) return;
      setActivityState((current) => ({
        ...current,
        [source]: { ...current[source], isLoading: true, error: null },
      }));
      try {
        const page = await getOnChainTransactionHistoryPage(address, ACTIVITY_PAGE_SIZE);
        if (!isCurrentRequest(source, address)) return;
        setActivityState((current) => ({
          ...current,
          [source]: {
            records: page.records.slice(0, ACTIVITY_PAGE_SIZE),
            pendingRecords: page.records.slice(ACTIVITY_PAGE_SIZE),
            isLoading: false,
            isLoadingMore: false,
            hasMore: page.records.length > ACTIVITY_PAGE_SIZE || Boolean(page.nextCursor),
            error: null,
            loadMoreError: null,
            nextCursor: page.nextCursor,
          },
        }));
      } catch (activityError) {
        if (!isCurrentRequest(source, address)) return;
        setActivityState((current) => ({
          ...current,
          [source]: {
            ...current[source],
            isLoading: false,
            error: formatErrorWithCode(activityError, t, 'errors.metaMaskActivity'),
          },
        }));
      }
    };

    if (activitySource === 'bubble-wallet') {
      await refreshBubbleWalletActivity();
    } else if (activitySource === 'world-app') {
      await refreshOnChainActivity('world-app', walletAddress);
    } else {
      await refreshOnChainActivity('metamask', metaMaskAddress);
    }
  }, [activitySource, metaMaskAddress, session, t, walletAddress]);

  const loadMoreBubbleWalletActivity = useCallback(async (): Promise<boolean> => {
    if (!session) return false;

    const current = activityState['bubble-wallet'];
    const lastRecord = current.records[current.records.length - 1];
    if (
      !current.hasMore
      || current.isLoading
      || current.isLoadingMore
      || activityLoadMoreInFlight.current['bubble-wallet']
      || !lastRecord
    ) return false;
    const requestGeneration = activityRequestGeneration.current;
    const requestUserId = session.user.id;
    const isCurrentRequest = () => activityRequestGeneration.current === requestGeneration
      && sessionRef.current?.user.id === requestUserId;
    if (!isCurrentRequest()) return false;
    activityLoadMoreInFlight.current['bubble-wallet'] = true;

    setActivityState((state) => ({
      ...state,
      'bubble-wallet': { ...state['bubble-wallet'], isLoadingMore: true, loadMoreError: null },
    }));

    try {
      const fetchedNextRecords = await getBubbleWalletSpendingActivity(BUBBLE_WALLET_ACTIVITY_FETCH_SIZE, {
        occurredAt: lastRecord.occurredAt,
        activityId: lastRecord.activityId,
      });
      if (!isCurrentRequest()) return false;
      const nextRecords = fetchedNextRecords.slice(0, ACTIVITY_PAGE_SIZE);
      const existingIds = new Set(current.records.map((record) => record.activityId));
      const uniqueNextRecords = nextRecords.filter((record) => !existingIds.has(record.activityId));
      setActivityState((state) => {
        return {
          ...state,
          'bubble-wallet': {
            ...state['bubble-wallet'],
            records: [...state['bubble-wallet'].records, ...uniqueNextRecords],
            isLoadingMore: false,
            hasMore: fetchedNextRecords.length > ACTIVITY_PAGE_SIZE && uniqueNextRecords.length > 0,
            loadMoreError: null,
          },
        };
      });
      return uniqueNextRecords.length > 0;
    } catch (activityError) {
      if (!isCurrentRequest()) return false;
      setActivityState((state) => ({
        ...state,
        'bubble-wallet': {
          ...state['bubble-wallet'],
          isLoadingMore: false,
          loadMoreError: formatErrorWithCode(activityError, t, 'errors.walletActivity'),
        },
      }));
      return false;
    } finally {
      activityLoadMoreInFlight.current['bubble-wallet'] = false;
    }
  }, [activityState, session, t]);

  const loadMoreOnChainActivity = useCallback(async (source: WalletBalanceSource): Promise<boolean> => {
    const current = activityState[source];
    const address = source === 'world-app' ? walletAddressRef.current : metaMaskAddressRef.current;
    if (
      !address
      || !current.hasMore
      || current.isLoading
      || current.isLoadingMore
      || activityLoadMoreInFlight.current[source]
      || (current.pendingRecords.length === 0 && !current.nextCursor)
    ) return false;

    const requestGeneration = activityRequestGeneration.current;
    const requestedAddress = address;
    const isCurrentRequest = () => activityRequestGeneration.current === requestGeneration
      && (source === 'world-app' ? walletAddressRef.current : metaMaskAddressRef.current) === requestedAddress;
    if (!isCurrentRequest()) return false;
    activityLoadMoreInFlight.current[source] = true;

    setActivityState((state) => ({
      ...state,
      [source]: { ...state[source], isLoadingMore: true, loadMoreError: null },
    }));

    try {
      if (current.pendingRecords.length > 0) {
        const nextRecords = current.pendingRecords.slice(0, ACTIVITY_PAGE_SIZE);
        const pendingRecords = current.pendingRecords.slice(ACTIVITY_PAGE_SIZE);
        const existingKeys = new Set(
          current.records.map((record) => `${record.hash.toLowerCase()}:${record.kind}:${record.tokenSymbol}:${record.direction}`),
        );
        const uniqueNextRecords = nextRecords.filter(
          (record) => !existingKeys.has(`${record.hash.toLowerCase()}:${record.kind}:${record.tokenSymbol}:${record.direction}`),
        );
        setActivityState((state) => ({
          ...state,
          [source]: {
            ...state[source],
            records: [...state[source].records, ...uniqueNextRecords],
            pendingRecords,
            isLoadingMore: false,
            hasMore: pendingRecords.length > 0 || Boolean(state[source].nextCursor),
            loadMoreError: null,
          },
        }));
        return uniqueNextRecords.length > 0;
      }

      const page = await getOnChainTransactionHistoryPage(address, ACTIVITY_PAGE_SIZE, current.nextCursor);
      if (!isCurrentRequest()) return false;
      const nextRecords = page.records.slice(0, ACTIVITY_PAGE_SIZE);
      const pendingRecords = page.records.slice(ACTIVITY_PAGE_SIZE);
      const existingKeys = new Set(
        current.records.map((record) => `${record.hash.toLowerCase()}:${record.kind}:${record.tokenSymbol}:${record.direction}`),
      );
      const uniqueNextRecords = nextRecords.filter(
        (record) => !existingKeys.has(`${record.hash.toLowerCase()}:${record.kind}:${record.tokenSymbol}:${record.direction}`),
      );
      setActivityState((state) => {
        return {
          ...state,
          [source]: {
            ...state[source],
            records: [...state[source].records, ...uniqueNextRecords],
            pendingRecords,
            isLoadingMore: false,
            hasMore: pendingRecords.length > 0 || Boolean(page.nextCursor),
            loadMoreError: null,
            nextCursor: page.nextCursor,
          },
        };
      });
      return uniqueNextRecords.length > 0;
    } catch (activityError) {
      if (!isCurrentRequest()) return false;
      setActivityState((state) => ({
        ...state,
        [source]: {
          ...state[source],
          isLoadingMore: false,
          loadMoreError: formatErrorWithCode(activityError, t, 'errors.metaMaskActivity'),
        },
      }));
      return false;
    } finally {
      activityLoadMoreInFlight.current[source] = false;
    }
  }, [activityState, t]);

  const handleActivityLoadMore = useCallback(async (source: ActivitySource) => {
    if (source === 'bubble-wallet') {
      await loadMoreBubbleWalletActivity();
      return;
    }
    await loadMoreOnChainActivity(source);
  }, [loadMoreBubbleWalletActivity, loadMoreOnChainActivity]);

  const syncMetaMaskAccount = useCallback(async () => {
    const address = await getConnectedMetaMaskAddress();
    setMetaMaskAddress(address);
    if (address) {
      setMetaMaskBalances({});
      setMetaMaskBalanceError(null);
    } else {
      setMetaMaskBalances({});
      setMetaMaskBalanceError(null);
    }
  }, []);

  const handleConnectMetaMask = useCallback(async () => {
    setIsMetaMaskBalancesLoading(true);
    setMetaMaskBalanceError(null);
    try {
      const address = await connectMetaMask();
      setMetaMaskAddress(address);
      setBalanceSource('metamask');
    } catch (connectError) {
      setMetaMaskBalanceError(formatErrorWithCode(connectError, t, 'errors.metaMaskConnect'));
    } finally {
      setIsMetaMaskBalancesLoading(false);
    }
  }, [t]);

  const handleDisconnectMetaMask = useCallback(async () => {
    setIsMetaMaskDisconnecting(true);
    setMetaMaskBalanceError(null);
    try {
      await disconnectMetaMask();
      setMetaMaskAddress(null);
      setMetaMaskBalances({});
      setBalanceSource('world-app');
    } catch (disconnectError) {
      setMetaMaskBalanceError(formatErrorWithCode(disconnectError, t, 'errors.metaMaskDisconnect'));
    } finally {
      setIsMetaMaskDisconnecting(false);
    }
  }, [t]);

  const refreshTokenPrices = useCallback(async (forceRefresh = false) => {
    setIsTokenPricesLoading(true);
    setTokenPriceError(null);
    try {
      const result = await getOnChainTokenPrices(forceRefresh);
      setTokenPrices(result.prices);
      setTokenPricesFetchedAt(result.fetchedAt);
    } catch (refreshError) {
      setTokenPriceError(formatErrorWithCode(refreshError, t, 'errors.priceQuery'));
    } finally {
      setIsTokenPricesLoading(false);
    }
  }, [t]);

  useEffect(() => {
    onChainBalanceRequestGeneration.current += 1;
    setOnChainBalances({});
    setIsBalancesLoading(false);
    setHasOnChainBalancesLoaded(false);
  }, [walletAddress]);

  useEffect(() => {
    metaMaskBalanceRequestGeneration.current += 1;
    setMetaMaskBalances({});
    setIsMetaMaskBalancesLoading(false);
    setHasMetaMaskBalancesLoaded(false);
  }, [metaMaskAddress]);

  useEffect(() => {
    if (activeTab !== 'on-chain-wallet') return;
    if (balanceSource === 'metamask') {
      void refreshMetaMaskBalances(metaMaskAddress);
      return;
    }
    void refreshOnChainBalances();
  }, [activeTab, balanceSource, metaMaskAddress, refreshMetaMaskBalances, refreshOnChainBalances]);

  useEffect(() => {
    let cancelled = false;
    const syncAccount = async () => {
      try {
        await syncMetaMaskAccount();
      } catch {
        if (!cancelled) {
          setMetaMaskAddress(null);
          setMetaMaskBalances({});
        }
      }
    };
    void syncAccount();

    let provider: Awaited<ReturnType<typeof getMetaMaskProvider>> | null = null;
    const handleProviderChange = () => {
      void syncAccount();
    };
    void getMetaMaskProvider()
      .then((connectedProvider) => {
        if (cancelled || !connectedProvider.on) return;
        provider = connectedProvider;
        connectedProvider.on('accountsChanged', handleProviderChange);
        connectedProvider.on('chainChanged', handleProviderChange);
      })
      .catch(() => {
        // The connect button will surface a user-facing error if the connector cannot initialize.
      });
    return () => {
      cancelled = true;
      provider?.removeListener?.('accountsChanged', handleProviderChange);
      provider?.removeListener?.('chainChanged', handleProviderChange);
    };
  }, [syncMetaMaskAccount]);

  useEffect(() => {
    if (activeTab !== 'on-chain-wallet') return;
    void refreshTokenPrices();
  }, [activeTab, refreshTokenPrices]);

  const refreshGameBalances = useCallback(async () => {
    if (!session?.user.id) {
      setBbgGameBalance(null);
      setFcashGameBalance(null);
      setBbgGameBalanceError(null);
      setFcashGameBalanceError(null);
      return;
    }

    setIsGameBalancesLoading(true);
    setBbgGameBalanceError(null);
    setFcashGameBalanceError(null);
    const [bbgResult, fcashResult] = await Promise.allSettled([
      getInGameBbgBalance(session.user.id),
      getFlight404FcashWallet(session),
    ]);

    if (bbgResult.status === 'fulfilled') {
      setBbgGameBalance(bbgResult.value);
    } else {
      setBbgGameBalance(null);
      setBbgGameBalanceError(formatErrorWithCode(bbgResult.reason, t, 'errors.bbgBalance'));
    }

    if (fcashResult.status === 'fulfilled') {
      setFcashGameBalance(fcashResult.value);
    } else {
      setFcashGameBalance(null);
      setFcashGameBalanceError(formatErrorWithCode(fcashResult.reason, t, 'errors.fcashBalance'));
    }

    setIsGameBalancesLoading(false);
  }, [session, t]);

  const refreshPendingWithdrawals = useCallback(async () => {
    if (!session?.user.id || !supabase) {
      setPendingWithdrawals([]);
      return;
    }
    try {
      setPendingWithdrawals(await getPendingWithdrawals());
    } catch {
      setPendingWithdrawals([]);
    }
  }, [session?.user.id]);

  const handleCancelWithdrawal = useCallback(async (withdrawal: PendingWithdrawal) => {
    if (cancellingWithdrawalId) return;
    setCancellingWithdrawalId(withdrawal.id);
    try {
      await cancelStaleWithdrawal(withdrawal.id);
      await Promise.all([refreshPendingWithdrawals(), refreshGameBalances()]);
    } finally {
      setCancellingWithdrawalId(null);
    }
  }, [cancellingWithdrawalId, refreshGameBalances, refreshPendingWithdrawals]);

  useEffect(() => {
    void refreshGameBalances();
  }, [refreshGameBalances]);

  useEffect(() => {
    void refreshPendingWithdrawals();
  }, [refreshPendingWithdrawals]);

  useEffect(() => {
    const swapPresetFromDeepLink = parseSwapDeepLink(window.location.search);
    if (swapPresetFromDeepLink) {
      setActiveTab('on-chain-wallet');
      setSwapPreset(swapPresetFromDeepLink);
      setIsSwapModalOpen(true);
      const params = new URLSearchParams(window.location.search);
      params.delete('modal');
      params.delete('fromToken');
      params.delete('toToken');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const requestedModal = params.get('modal');
    const token = params.get('token');
    if (token && token !== 'BBG') return;
    if (requestedModal === 'deposit' || requestedModal === 'withdraw') {
      setBbgTransferMode(requestedModal);
      params.delete('modal');
      params.delete('token');
      const query = params.toString();
      window.history.replaceState({}, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
    }
  }, [session]);

  useEffect(() => {
    if (activeTab !== 'activity') return;
    void refreshActivity();
  }, [activeTab, refreshActivity]);

  useEffect(() => {
    const onFocus = () => {
      if (activeTab === 'on-chain-wallet') {
        if (balanceSource === 'metamask') {
          void refreshMetaMaskBalances(metaMaskAddress);
        } else {
          void refreshOnChainBalances();
        }
        void refreshTokenPrices();
      }
      void refreshGameBalances();
      void refreshPendingWithdrawals();
      if (activeTab === 'activity') void refreshActivity();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [activeTab, balanceSource, metaMaskAddress, refreshActivity, refreshGameBalances, refreshMetaMaskBalances, refreshOnChainBalances, refreshPendingWithdrawals, refreshTokenPrices]);

  const handleAccountButton = () => {
    if (session) {
      setActiveTab('settings');
      return;
    }
    void signIn();
  };

  const sendTokenOptions = useMemo<SendTokenOption[]>(
    () => tokenCards.map(({ symbol, label }) => ({
      symbol,
      label,
      balance: onChainBalances[symbol],
    })),
    [onChainBalances],
  );

  const swapTokenOptions = useMemo<SwapTokenOption[]>(
    () => tokenCards.map(({ symbol, label }) => ({
      symbol,
      label,
      balance: onChainBalances[symbol],
    })),
    [onChainBalances],
  );

  const metaMaskSwapTokenOptions = useMemo<SwapTokenOption[]>(
    () => tokenCards.map(({ symbol, label }) => ({
      symbol,
      label,
      balance: metaMaskBalances[symbol],
    })),
    [metaMaskBalances],
  );

  const metaMaskSendTokenOptions = useMemo<SendTokenOption[]>(
    () => tokenCards.map(({ symbol, label }) => ({
      symbol,
      label,
      balance: metaMaskBalances[symbol],
    })),
    [metaMaskBalances],
  );

  const refreshActiveBalances = useCallback(async () => {
    if (balanceSource === 'metamask') {
      await refreshMetaMaskBalances(metaMaskAddress);
      return;
    }
    await refreshOnChainBalances();
  }, [balanceSource, metaMaskAddress, refreshMetaMaskBalances, refreshOnChainBalances]);

  const activeBalances = balanceSource === 'metamask'
    ? metaMaskBalances
    : onChainBalances;
  const activeBalancesLoading = balanceSource === 'metamask'
    ? !hasMetaMaskBalancesLoaded
    : isAuthLoading || !hasOnChainBalancesLoaded;
  const activeBalancesRefreshing = balanceSource === 'metamask'
    ? isMetaMaskBalancesLoading
    : isBalancesLoading;
  const activeAssetsLoading = activeBalancesLoading || activeBalancesRefreshing;
  const activeWalletAddress = balanceSource === 'metamask'
    ? metaMaskAddress
    : walletAddress;
  const activeSendTokenOptions = balanceSource === 'metamask'
    ? metaMaskSendTokenOptions
    : sendTokenOptions;
  const bubbleGameLink = resolveBubbleGameLink(isWorldApp, bubbleGameDeepLink);

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand-lockup">
          <img className="brand-mark" src="/image/bubblewallet_logo_05.png" alt="Bubble Wallet" />
          <div>
            <p className="brand-name">Bubble Wallet</p>
          <p className="brand-subtitle">GGmomomo Games</p>
          </div>
        </div>
        <div className="account-login-control">
          {!session && <span className="login-hint">LOGIN</span>}
          <button
            className={`account-button ${isWorldApp ? 'is-world-app' : 'is-browser'}${isWorldApp && isSigningIn ? ' is-signing-in' : ''}`}
            type="button"
            onClick={handleAccountButton}
            disabled={isSigningIn}
            aria-busy={isSigningIn}
            aria-label={session ? t('navigation.settings') : t('settings.account')}
          >
            {isWorldApp ? (
              <img src="/image/world-id-logo.png" alt="World ID" />
            ) : (
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm7 8a7 7 0 0 0-14 0" />
              </svg>
            )}
          </button>
        </div>
      </header>

      <main className="main-content">
        {error && <p className="auth-error account-feedback" role="alert">{error}</p>}

        {(activeTab === 'in-game-wallet' || activeTab === 'on-chain-wallet') && (
          <WalletAssetsPage
            view={activeTab}
            balances={activeBalances}
            isLoading={activeAssetsLoading}
            onRefresh={() => {
              void refreshActiveBalances();
              void refreshTokenPrices(true);
            }}
            walletAddress={activeWalletAddress}
            balanceSource={balanceSource}
            metaMaskAddress={metaMaskAddress}
            isMetaMaskLoading={isMetaMaskBalancesLoading}
            metaMaskError={metaMaskBalanceError}
            onSelectBalanceSource={setBalanceSource}
            onConnectMetaMask={handleConnectMetaMask}
            onOpenSendModal={() => {
              setSendTokenSymbol(null);
              setIsSendTokenModalOpen(true);
            }}
            onOpenSwapModal={() => {
              setSwapPreset(null);
              setIsSwapModalOpen(true);
            }}
            onOpenBbtInfoModal={() => setIsBbtInfoModalOpen(true)}
            onOpenBbgInfoModal={() => setIsBbgInfoModalOpen(true)}
            onOpenGgmomomoInfoModal={() => setIsGgmomomoInfoModalOpen(true)}
            onOpenWldInfoModal={() => setIsWldInfoModalOpen(true)}
            onOpenBubbleGameUniverseModal={() => setIsBubbleGameUniverseModalOpen(true)}
            onOpenFlight404InfoModal={() => setIsFlight404InfoModalOpen(true)}
            bubbleGameLink={bubbleGameLink}
            onOpenReceiveModal={() => setIsReceiveAddressModalOpen(true)}
            onOpenClaimModal={() => setIsClaimModalOpen(true)}
            bbgGameBalance={bbgGameBalance}
            fcashGameBalance={fcashGameBalance}
            isGameBalancesLoading={isGameBalancesLoading}
            bbgGameBalanceError={bbgGameBalanceError}
            fcashGameBalanceError={fcashGameBalanceError}
            onRefreshGameBalances={() => void refreshGameBalances()}
            onOpenBbgTransfer={setBbgTransferMode}
            pendingWithdrawals={pendingWithdrawals}
            cancellingWithdrawalId={cancellingWithdrawalId}
            onCancelWithdrawal={(withdrawal) => void handleCancelWithdrawal(withdrawal)}
            prices={tokenPrices}
            isPricesLoading={isTokenPricesLoading}
            priceError={tokenPriceError}
            priceFetchedAt={tokenPricesFetchedAt}
          />
        )}
        {activeTab === 'activity' && (
          <Activity
            worldAppAddress={walletAddress}
            metaMaskAddress={metaMaskAddress}
            activityState={activityState}
            isAuthenticated={Boolean(session)}
            onLoadMoreActivity={handleActivityLoadMore}
            selectedSource={activitySource}
            onSelectSource={setActivitySource}
            onConnectMetaMask={handleConnectMetaMask}
            isMetaMaskLoading={isMetaMaskBalancesLoading}
            metaMaskError={metaMaskBalanceError}
          />
        )}
        {activeTab === 'settings' && (
          <Settings
            walletAddress={walletAddress}
            metaMaskAddress={metaMaskAddress}
            isMetaMaskLoading={isMetaMaskBalancesLoading}
            isMetaMaskDisconnecting={isMetaMaskDisconnecting}
            metaMaskError={metaMaskBalanceError}
            onConnectMetaMask={handleConnectMetaMask}
            onDisconnectMetaMask={handleDisconnectMetaMask}
          />
        )}

      </main>

      <SendTokenModal
        isOpen={isSendTokenModalOpen}
        initialSymbol={sendTokenSymbol}
        options={activeSendTokenOptions}
        isWorldApp={isWorldApp}
        authUserId={session?.user.id ?? null}
        balanceSource={balanceSource}
        walletAddress={activeWalletAddress}
        isMetaMaskLoading={isMetaMaskBalancesLoading}
        metaMaskError={metaMaskBalanceError}
        onConnectMetaMask={handleConnectMetaMask}
        walletAppDeepLink={walletAppDeepLink}
        onClose={() => {
          setIsSendTokenModalOpen(false);
          setSendTokenSymbol(null);
        }}
        onSubmitted={() => {
          void refreshActiveBalances();
          void refreshActivity();
        }}
      />

      <SwapModal
        isOpen={isSwapModalOpen}
        initialInputSymbol={swapPreset?.inputSymbol}
        initialOutputSymbol={swapPreset?.outputSymbol}
        authUserId={session?.user.id ?? null}
        walletAddress={walletAddress}
        isWorldApp={isWorldApp}
        options={swapTokenOptions}
        isBalanceLoading={activeBalancesLoading}
        balanceSource={balanceSource}
        metaMaskAddress={metaMaskAddress}
        metaMaskOptions={metaMaskSwapTokenOptions}
        isMetaMaskLoading={isMetaMaskBalancesLoading}
        metaMaskError={metaMaskBalanceError}
        onConnectMetaMask={handleConnectMetaMask}
        onClose={() => {
          setIsSwapModalOpen(false);
          setSwapPreset(null);
        }}
        onSubmitted={() => {
          void refreshActiveBalances();
          void refreshActivity();
        }}
      />

      <BbtInfoModal
        isOpen={isBbtInfoModalOpen}
        furnaceDeepLink={bubbleGameFurnaceDeepLink}
        onClose={() => setIsBbtInfoModalOpen(false)}
        onOpenPurchase={() => {
          setIsBbtInfoModalOpen(false);
          setSwapPreset({ inputSymbol: 'WLD', outputSymbol: 'BBT' });
          setIsSwapModalOpen(true);
        }}
      />

      <BbgInfoModal
        isOpen={isBbgInfoModalOpen}
        bubbleGameLink={bubbleGameLink}
        onClose={() => setIsBbgInfoModalOpen(false)}
      />

      <GgmomomoInfoModal
        isOpen={isGgmomomoInfoModalOpen}
        onClose={() => setIsGgmomomoInfoModalOpen(false)}
        onOpenPurchase={() => {
          setIsGgmomomoInfoModalOpen(false);
          setSwapPreset({ inputSymbol: 'WLD', outputSymbol: 'GGMOMOMO' });
          setIsSwapModalOpen(true);
        }}
      />

      <WldInfoModal
        isOpen={isWldInfoModalOpen}
        onClose={() => setIsWldInfoModalOpen(false)}
        onOpenTopUp={() => {
          setIsWldInfoModalOpen(false);
          setIsReceiveAddressModalOpen(true);
        }}
      />

      <BubbleGameUniverseModal
        isOpen={isBubbleGameUniverseModalOpen}
        bubbleGameLink={bubbleGameLink}
        onClose={() => setIsBubbleGameUniverseModalOpen(false)}
      />

      <Flight404InfoModal
        isOpen={isFlight404InfoModalOpen}
        onClose={() => setIsFlight404InfoModalOpen(false)}
      />

      <ReceiveAddressModal
        isOpen={isReceiveAddressModalOpen}
        walletAddress={activeWalletAddress}
        walletSource={balanceSource}
        onClose={() => setIsReceiveAddressModalOpen(false)}
      />

      <ClaimModal
        isOpen={isClaimModalOpen}
        authUserId={session?.user.id ?? null}
        walletAddress={walletAddress}
        isWorldApp={isWorldApp}
        onClose={() => setIsClaimModalOpen(false)}
        onSubmitted={() => {
          void refreshOnChainBalances();
          void refreshActivity();
        }}
      />

      <BbgTransferModal
        mode={bbgTransferMode ?? 'deposit'}
        isOpen={bbgTransferMode !== null}
        bbgBalance={bbgGameBalance}
        onChainBbgBalance={onChainBalances.BBG ?? null}
        wldBalance={onChainBalances.WLD ?? null}
        isWorldApp={isWorldApp}
        onClose={() => setBbgTransferMode(null)}
        onSuccess={async () => {
          await Promise.all([
            refreshGameBalances(),
            refreshOnChainBalances(),
            refreshPendingWithdrawals(),
          ]);
        }}
      />

      <WorldTransactionToast />

      <nav className="bottom-nav" aria-label={t('navigation.main')}>
        {tabs.map((tab) => (
          <button
            className={activeTab === tab.id ? 'nav-item is-active' : 'nav-item'}
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="nav-icon"><NavigationIcon name={tab.icon} /></span>
            <span>{t(`navigation.${tab.labelKey}`)}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
